import { IconType } from 'react-icons'
import {
  TbDatabase,
  TbBolt,
  TbTransform,
  TbLayersLinked,
  TbPlaylistAdd,
  TbChartHistogram,
  TbDots,
  TbManualGearbox,
  TbGitFork,
  TbClick,
} from 'react-icons/tb'
import { DOCS_LINK } from 'routes/constants'

export type BuildingBlocksInitialDataType = {
  Icon: IconType
  rotated?: boolean
  title: string
  description: string
  href: string
}

export type BuildingBlocksMarkupDataType = BuildingBlocksInitialDataType & {
  puzzleJigs: Record<string, string[]>
}

const buildingBlocksInitialData: BuildingBlocksInitialDataType[] = [
  {
    Icon: TbClick,
    title: 'Actions',
    description: 'Make requests and queries to external apps',
    href: `${DOCS_LINK}/actions`,
  },
  {
    Icon: TbBolt,
    title: 'Events',
    description: 'Know when something changes in external apps',
    href: `${DOCS_LINK}/events`,
  },
  {
    Icon: TbManualGearbox,
    rotated: true,
    title: 'Flows',
    description: 'Multi-step integrations with branching and loops',
    href: `${DOCS_LINK}/flows`,
  },
  {
    Icon: TbDatabase,
    title: 'Data Collections',
    description: 'Read, write, and search data in external apps',
    href: `${DOCS_LINK}/data-collections`,
  },
  {
    Icon: TbManualGearbox,
    title: 'Unified Data Models',
    description: 'Work with data in a consistent way',
    href: `${DOCS_LINK}/universal-data-models`,
  },
  {
    Icon: TbTransform,
    title: 'Field Mappings',
    description: 'Apply user-defined data transformations',
    href: `${DOCS_LINK}/field-mappings`,
  },
  {
    Icon: TbLayersLinked,
    title: 'Data Links',
    description: 'Match objects in your app with external apps',
    href: `${DOCS_LINK}/data-links`,
  },
  {
    Icon: TbPlaylistAdd,
    title: 'Custom Fields',
    description: 'Deal with customer-specific application setups',
    href: `${DOCS_LINK}/custom-fields`,
  },
  {
    Icon: TbGitFork,
    rotated: true,
    title: 'Data Schemas',
    description: 'Use custom data structures in integrations',
    href: `${DOCS_LINK}/internal-data-schemas`,
  },
  {
    Icon: TbBolt,
    title: 'Internal Events',
    description: 'Trigger integrations on events in your app',
    href: `${DOCS_LINK}/internal-events`,
  },
  {
    Icon: TbChartHistogram,
    title: 'Activity Logs',
    description: 'See when things break and why',
    href: `${DOCS_LINK}/monitoring-troubleshooting`,
  },
  {
    Icon: TbDots,
    title: 'And more...',
    description: 'This most extensive integration API on the market',
    href: `${DOCS_LINK}`,
  },
]

const getPuzzleJigsXl = getPuzzleJigs(4, 'xl')
const getPuzzleJigsLg = getPuzzleJigs(3, 'lg')
const getPuzzleJigsMd = getPuzzleJigs(2, 'md')
const getPuzzleJigsSm = getPuzzleJigs(1, 'sm')

// Generate puzzle jigs for each building block
// The jigs are used to connect the building blocks and should not intersect with each other
export const buildingBlocksMarkupData: BuildingBlocksMarkupDataType[] = pipe(
  buildingBlocksInitialData,
  getPuzzleJigsXl,
  getPuzzleJigsLg,
  getPuzzleJigsMd,
  getPuzzleJigsSm,
)

function pipe(arr: BuildingBlocksInitialDataType[], ...fns) {
  return fns.reduce((acc, curr) => curr(acc), arr)
}

function getPuzzleJigs(cols: number, key: string) {
  return function (arr: BuildingBlocksMarkupDataType[]) {
    return arr.reduce((acc, curr, index) => {
      const isFirstCol = index % cols === 0
      const isLastCol = index % cols === cols - 1
      const isFirstRow = index < cols
      const isLastRow = index >= arr.length - cols

      const jigsOnCellLeft = arr[index - 1]?.puzzleJigs?.[key] ?? []
      const jigOnCellAbove = arr[index - cols]?.puzzleJigs?.[key] ?? []

      const jigLocations: string[] = []

      // If the cell to the left does not have a jig on the right
      // then the cell must have a jig on the left to connect to the cell on the left
      if (!isFirstCol && !jigsOnCellLeft.includes('right')) {
        jigLocations.push('left')
      }

      // If the cell above does not have a jig on the bottom
      // then the cell must have a jig on the top to connect to the cell above
      if (!isFirstRow && !jigOnCellAbove.includes('bottom')) {
        jigLocations.push('top')
      }

      // Possible connections
      if (!isLastCol) {
        pushOrNot(jigLocations, 'right')
      }

      if (!isLastRow) {
        pushOrNot(jigLocations, 'bottom')
      }

      curr.puzzleJigs = curr.puzzleJigs ?? {}
      curr.puzzleJigs[key] = jigLocations
      acc.push(curr)
      return acc
    }, [] as BuildingBlocksMarkupDataType[])
  }
}

function pushOrNot(array, item) {
  if (Math.random() > 0.5) {
    array.push(item)
  }
  return array
}
